import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode } from 'react';

type EditorLinkPickerContextValue = {
  registerOpen: (open: () => void) => () => void;
  open: () => void;
};

const EditorLinkPickerContext = createContext<EditorLinkPickerContextValue | null>(null);

export function EditorLinkPickerProvider({ children }: { children: ReactNode }) {
  const openerRef = useRef<(() => void) | null>(null);

  const registerOpen = useCallback((open: () => void) => {
    openerRef.current = open;
    return () => {
      if (openerRef.current === open) {
        openerRef.current = null;
      }
    };
  }, []);

  const open = useCallback(() => {
    openerRef.current?.();
  }, []);

  const value = useMemo(() => ({ registerOpen, open }), [open, registerOpen]);

  return (
    <EditorLinkPickerContext.Provider value={value}>{children}</EditorLinkPickerContext.Provider>
  );
}

export function useEditorLinkPicker(): EditorLinkPickerContextValue {
  const ctx = useContext(EditorLinkPickerContext);
  if (!ctx) {
    throw new Error('useEditorLinkPicker must be used within EditorLinkPickerProvider');
  }
  return ctx;
}
